import { nanoid } from 'nanoid'

export type PayloadNodeType = 'object' | 'array' | 'string' | 'number' | 'boolean'
export type PayloadParentType = 'object' | 'array'

export interface PayloadNode {
  id: string
  key: string
  type: PayloadNodeType
  value: string | number | boolean | null
  children: PayloadNode[]
  collapsed: boolean
}

export interface PayloadValidationIssue {
  nodeId: string
  field: 'key' | 'value'
  message: string
}

export interface PayloadTypeItem {
  title: string
  value: PayloadNodeType
  icon: string
}

export const PAYLOAD_TYPE_ITEMS: PayloadTypeItem[] = [
  { title: 'Text', value: 'string', icon: 'mdi-format-text' },
  { title: 'Number', value: 'number', icon: 'mdi-numeric' },
  { title: 'Yes / No', value: 'boolean', icon: 'mdi-toggle-switch-outline' },
  { title: 'Group', value: 'object', icon: 'mdi-code-json' },
  { title: 'List', value: 'array', icon: 'mdi-format-list-bulleted' }
]

export function createPayloadNode(type: PayloadNodeType = 'string', key = ''): PayloadNode {
  return {
    id: nanoid(),
    key,
    type,
    value: defaultValueForPayloadType(type),
    children: [],
    collapsed: false
  }
}

export function defaultValueForPayloadType(type: PayloadNodeType): string | number | boolean | null {
  switch (type) {
    case 'number':
      return ''
    case 'boolean':
      return true
    default:
      return ''
  }
}

export function jsonObjectToPayloadTree(value: Record<string, unknown>): PayloadNode[] {
  return Object.entries(value).map(([key, childValue]) => jsonValueToPayloadNode(key, childValue))
}

export function jsonValueToPayloadNode(key: string, value: unknown): PayloadNode {
  if (Array.isArray(value)) {
    return {
      id: nanoid(),
      key,
      type: 'array',
      value: '',
      children: value.map((item) => jsonValueToPayloadNode('', item)),
      collapsed: false
    }
  }

  if (typeof value === 'object' && value !== null) {
    return {
      id: nanoid(),
      key,
      type: 'object',
      value: '',
      children: jsonObjectToPayloadTree(value as Record<string, unknown>),
      collapsed: false
    }
  }

  if (typeof value === 'number') {
    return {
      id: nanoid(),
      key,
      type: 'number',
      value: Number.isFinite(value) ? value : '',
      children: [],
      collapsed: false
    }
  }

  if (typeof value === 'boolean') {
    return {
      id: nanoid(),
      key,
      type: 'boolean',
      value,
      children: [],
      collapsed: false
    }
  }

  return {
    id: nanoid(),
    key,
    type: 'string',
    value: typeof value === 'string' ? value : String(value),
    children: [],
    collapsed: false
  }
}

export function payloadTreeToJson(nodes: PayloadNode[]): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const node of nodes) {
    const key = node.key.trim()
    if (!key) continue
    result[key] = payloadNodeToJsonValue(node)
  }
  return result
}

export function payloadNodeToJsonValue(node: PayloadNode): unknown {
  switch (node.type) {
    case 'object':
      return payloadTreeToJson(node.children)
    case 'array':
      return node.children.map((child) => payloadNodeToJsonValue(child))
    case 'number': {
      const numberValue = typeof node.value === 'number' ? node.value : Number(String(node.value).trim())
      if (!Number.isFinite(numberValue)) {
        throw new Error(`Invalid number for key "${node.key}"`)
      }
      return numberValue
    }
    case 'boolean':
      if (typeof node.value !== 'boolean') {
        throw new Error(`Invalid yes/no value for key "${node.key}"`)
      }
      return Boolean(node.value)
    case 'string':
    default:
      return typeof node.value === 'string' ? node.value : String(node.value ?? '')
  }
}

export function setPayloadNodeType(node: PayloadNode, type: PayloadNodeType): PayloadNode {
  const currentTypeIsContainer = node.type === 'object' || node.type === 'array'
  const nextTypeIsContainer = type === 'object' || type === 'array'
  return {
    ...node,
    type,
    value: node.value,
    children: nextTypeIsContainer && currentTypeIsContainer ? node.children : [],
    collapsed: false
  }
}

export function addPayloadChild(parent: PayloadNode): PayloadNode {
  const child = createPayloadNode('string')
  parent.children.push(child)
  parent.collapsed = false
  return child
}

export function movePayloadNode(nodes: PayloadNode[], fromIndex: number, toIndex: number): void {
  if (fromIndex < 0 || fromIndex >= nodes.length || toIndex < 0 || toIndex >= nodes.length) return
  const [item] = nodes.splice(fromIndex, 1)
  nodes.splice(toIndex, 0, item)
}

export function duplicateObjectKeys(nodes: PayloadNode[]): Set<string> {
  const counts = new Map<string, number>()
  for (const node of nodes) {
    const key = node.key.trim()
    if (!key) continue
    counts.set(key, (counts.get(key) || 0) + 1)
  }
  return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([key]) => key))
}

export function isValidNumberValue(value: string | number | boolean | null): boolean {
  if (typeof value === 'number') return Number.isFinite(value)
  if (typeof value !== 'string') return false
  const trimmed = value.trim()
  return trimmed.length > 0 && Number.isFinite(Number(trimmed))
}

export function validatePayloadNodes(nodes: PayloadNode[], parentType: PayloadParentType = 'object'): PayloadValidationIssue[] {
  const issues: PayloadValidationIssue[] = []
  const duplicateKeys = parentType === 'object' ? duplicateObjectKeys(nodes) : new Set<string>()

  for (const node of nodes) {
    if (parentType === 'object') {
      const key = node.key.trim()
      if (!key) {
        issues.push({ nodeId: node.id, field: 'key', message: 'Key is required' })
      } else if (duplicateKeys.has(key)) {
        issues.push({ nodeId: node.id, field: 'key', message: 'Key must be unique' })
      }
    }

    if (node.type === 'number' && !isValidNumberValue(node.value)) {
      issues.push({ nodeId: node.id, field: 'value', message: 'Enter a valid number' })
    }

    if (node.type === 'boolean' && typeof node.value !== 'boolean') {
      issues.push({ nodeId: node.id, field: 'value', message: 'Choose Yes or No' })
    }

    if (node.type === 'object') {
      issues.push(...validatePayloadNodes(node.children, 'object'))
    } else if (node.type === 'array') {
      issues.push(...validatePayloadNodes(node.children, 'array'))
    }
  }

  return issues
}

export function validationMessagesForNode(
  issues: PayloadValidationIssue[],
  nodeId: string,
  field: 'key' | 'value'
): string[] {
  return issues
    .filter((issue) => issue.nodeId === nodeId && issue.field === field)
    .map((issue) => issue.message)
}

export function containsNullPayloadValue(value: unknown): boolean {
  if (value === null) return true
  if (Array.isArray(value)) return value.some((item) => containsNullPayloadValue(item))
  if (typeof value === 'object' && value !== null) {
    return Object.values(value).some((childValue) => containsNullPayloadValue(childValue))
  }
  return false
}
